import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const ContactSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const nom = (formData.get('nom') as string) ?? '';
    const prenom = (formData.get('prenom') as string) ?? '';
    const email = (formData.get('email') as string) ?? '';
    const message = (formData.get('message') as string) ?? '';

    setStatus('sending');
    try {
      await emailjs.send(
        'service_hdov8s6',
        'template_6jw7ana',
        {
          from_name: `${prenom} ${nom}`,
          nom,
          prenom,
          email,
          reply_to: email,
          message,
        },
        'y459mQFz3zIKOmCR8'
      );
      setStatus('sent');
      form.reset();
    } catch (err: any) {
      const msg = err?.message || err?.text || JSON.stringify(err);
      console.error('EmailJS error:', err);
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  return (
    <section className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="h-px w-6 bg-brand-coral" />
              <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">Parlons de votre projet</span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite leading-tight mb-6">
              Donnons vie à <em>vos idées</em>
            </h2>
            <p className="text-brand-gray leading-relaxed mb-8">
              Que ce soit pour un événement inoubliable ou un intérieur élégant, notre équipe est là pour vous accompagner. Contactez-nous dès aujourd'hui.
            </p>

            {/* Contact info */}
            <ul className="space-y-4">
              {[
                { icon: 'ri-mail-line', text: 'hello@evcodesign-ci.com', href: 'mailto:hello@evcodesign-ci.com' },
                { icon: 'ri-instagram-line', text: '@evcodesign.ci', href: 'https://www.instagram.com/evcodesign.ci?igsh=emZmeWx2NXdmMmw1&utm_source=qr' },
                { icon: 'ri-map-pin-line', text: 'Abidjan, Côte d\'Ivoire', href: '#' },
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-beige">
                    <i className={`${item.icon} text-brand-fuchsia text-base`} />
                  </div>
                  <a
                    href={item.href}
                    className="text-sm text-brand-gray hover:text-brand-fuchsia transition-colors"
                    rel={item.href.startsWith('http') ? 'nofollow noopener noreferrer' : undefined}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Right – Form */}
          <div
            ref={ref}
            className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-brand-beige rounded-2xl p-8">
              <h3 className="font-serif text-2xl text-brand-anthracite mb-6">Envoyez-nous un message</h3>

              {status === 'sent' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-fuchsia/10 mb-4">
                    <i className="ri-check-line text-brand-fuchsia text-3xl" />
                  </div>
                  <h4 className="font-serif text-xl text-brand-anthracite mb-2">Message envoyé !</h4>
                  <p className="text-brand-gray text-sm">Nous vous répondrons dans les plus brefs délais.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 text-sm text-brand-fuchsia hover:text-brand-coral transition-colors cursor-pointer"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-brand-coral uppercase tracking-wide mb-2">Nom</label>
                      <input
                        name="nom"
                        type="text"
                        required
                        placeholder="Votre nom"
                        className="w-full px-0 py-2 text-sm text-brand-anthracite bg-transparent border-b-2 border-brand-anthracite/20 focus:border-brand-fuchsia outline-none transition-colors placeholder-brand-gray/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-brand-coral uppercase tracking-wide mb-2">Prénom</label>
                      <input
                        name="prenom"
                        type="text"
                        required
                        placeholder="Votre prénom"
                        className="w-full px-0 py-2 text-sm text-brand-anthracite bg-transparent border-b-2 border-brand-anthracite/20 focus:border-brand-fuchsia outline-none transition-colors placeholder-brand-gray/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-coral uppercase tracking-wide mb-2">Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="votre@email.com"
                      className="w-full px-0 py-2 text-sm text-brand-anthracite bg-transparent border-b-2 border-brand-anthracite/20 focus:border-brand-fuchsia outline-none transition-colors placeholder-brand-gray/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-coral uppercase tracking-wide mb-2">Message</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Décrivez votre projet..."
                      className="w-full px-0 py-2 text-sm text-brand-anthracite bg-transparent border-b-2 border-brand-anthracite/20 focus:border-brand-fuchsia outline-none transition-colors resize-none placeholder-brand-gray/50"
                    />
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-xs">
                      {errorMsg || 'Une erreur est survenue. Veuillez réessayer.'}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full py-3 rounded-full text-white text-sm font-medium whitespace-nowrap transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
                  >
                    {status === 'sending' ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
